import { Button } from "@/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { ChevronsUpDown } from "lucide-react";
import { FC, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

const DeviseSelect: FC<{
  options: any;
  defaultValue: number;
  onChange: (value: number) => void;
}> = ({ options, defaultValue, onChange }) => {
  const scrollableRef = useRef<HTMLDivElement | null>(null);
  const [open, setOpen] = useState(false);
  const [value, setValue] = useState(
    options.find((option) => option.value === defaultValue)?.label
  );
  const { t } = useTranslation("submit", {
    keyPrefix: "project.devise",
  });

  const onClickOutside = (ele, cb) => {
    document.addEventListener('click', event => {
      if (!ele.contains(event.target)) cb();
    });
  };

  useEffect(() => {
    onChange(options.find((option) => option.label === value)?.value);
  }, [value]);

  useEffect(() => {
    if (!open) {
      // remove all event click listener document
      document.removeEventListener('click', () => {});

      return; // Si le popover est fermé, on ne fait rien
    }

    setTimeout(() => {
      onClickOutside(document.getElementById('toOpenDevise'), () => {
        setOpen(false);
      });
    }, 200);

    const observer = new MutationObserver(() => {
      const element = document.querySelector(".div-a-scroller");
      if (element) {
        // Si l'élément est détecté, on l'assigne
        scrollableRef.current = element as HTMLDivElement;

        const handleWheel = (event: WheelEvent) => {
          event.stopPropagation(); // Bloque le scroll du parent
        };

        element.addEventListener("wheel", handleWheel, { passive: false });

        return () => {
          element.removeEventListener("wheel", handleWheel);
        };
      }
    });

    observer.observe(document.body, { childList: true, subtree: true });

    return () => {
      observer.disconnect();
      if (scrollableRef.current) {
        scrollableRef.current.removeEventListener("wheel", (event: WheelEvent) => {
          event.stopPropagation();
        });
      }
    };
  }, [open]); // L'effet se déclenche à chaque ouverture

  return (
    <Popover open={open}>
      <PopoverTrigger asChild>
        <Button
          id="toOpenDevise"
          variant="outline"
          role="combobox"
          aria-expanded={open}
          onClick={(e) => {
            setOpen(!open)
          }}
          className="ml-2 mt-1 w-[200px] justify-between overflow-hidden text-ellipsis rounded-md border border-input bg-background px-3 py-2 text-sm font-normal"
        >
          {value
            ? options.find((option) => option.label === value)?.label
            : t("Devise")}
          <ChevronsUpDown className="ml-2 size-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[200px] p-0 pointer-events-auto div-a-scroller">
        <Command>
          <CommandInput placeholder={t("Rechercher une devise...")} />
          <CommandList>
            <CommandEmpty>{t("Aucune devise.")}</CommandEmpty>
            <CommandGroup>
              {options.map((option) => (
                <CommandItem
                  key={option.value}
                  value={option.label}
                  onSelect={(currentValue) => {
                    setValue(currentValue);
                    setOpen(false);
                  }}
                  className="cursor-pointer data-[selected='true']:bg-slate-200 data-[selected='true']:text-black"
                >
                  <div className="truncate">{option.label}</div>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};

export default DeviseSelect;
